import { cn } from "@newxsystems/shared";
import type { BlockAlignment, CallToActionImageWidth } from "@newxsystems/types";
import { BlockActions } from "../../../shared/components/BlockActions/BlockActions";
import { BlockContainer } from "../../../shared/components/BlockContainer/BlockContainer";
import { BlockImage } from "../../../shared/components/BlockImage/BlockImage";
import { BlockRichText } from "../../../shared/components/BlockRichText/BlockRichText";
import type { ICallToActionStackedProps } from "./CallToActionStacked.props";

const alignmentClasses: Record<BlockAlignment, string> = {
  start: "items-start text-start",
  center: "items-center text-center",
};

const imageWidthClasses: Record<CallToActionImageWidth, string> = {
  small: "w-1/2",
  medium: "w-3/4",
  large: "w-full",
};

export const CallToActionStacked = ({
  alignment = "start",
  anchorId,
  buttons,
  description,
  eyebrow,
  heading,
  image,
  imageWidth = "medium",
  parentClass,
  spacing,
}: ICallToActionStackedProps) => {
  const resolvedAlignment = alignment ?? "start";
  const resolvedImageWidth = imageWidth ?? "medium";
  const centered = alignment === "center";

  return (
    <BlockContainer anchorId={anchorId} parentClass={parentClass} spacing={spacing}>
      <div className={cn("flex min-w-0 flex-col gap-6", alignmentClasses[resolvedAlignment])}>
        {eyebrow ? <p className="font-mono text-[12px]/[1.45]">{eyebrow}</p> : null}
        {image ? (
          <div className={imageWidthClasses[resolvedImageWidth]}>
            <BlockImage
              aspectRatioClassName="overflow-hidden rounded-[14px] border border-foreground"
              effectiveAspectRatio={image.aspectRatio ?? "16:9"}
              image={image}
              imageClassName="object-cover"
            />
          </div>
        ) : null}
        {heading ? (
          <h2
            className={cn(
              "text-balance text-[34px]/[1] font-semibold tracking-[-0.025em] [overflow-wrap:anywhere] md:text-[44px]",
              !centered && imageWidth === "large" ? "max-w-none" : "",
              !centered && imageWidth === "medium" ? "max-w-[30ch]" : "",
              !centered && imageWidth === "small" ? "max-w-[16ch]" : "",
              centered && "max-w-[16ch]",
            )}
          >
            {heading}
          </h2>
        ) : null}
        {description ? (
          <BlockRichText
            className="max-w-[62ch] text-base/[1.55] text-pretty text-onSurface-500 md:text-[17px] [&_a]:underline [&_ol]:ml-5 [&_ol]:list-decimal [&_p+p]:mt-4 [&_ul]:ml-5 [&_ul]:list-disc"
            content={description}
          />
        ) : null}
        {buttons?.length ? (
          <BlockActions actions={buttons} alignment={resolvedAlignment} className="mt-2" />
        ) : null}
      </div>
    </BlockContainer>
  );
};

CallToActionStacked.displayName = "CallToAction::CallToActionStacked";
